import { Color, EventDispatcher } from "three";
import { CameraUtils } from "./CameraUtils.js";
export class MultiViewsElement {
    camera;
    visible;
    scene;
    composer;
    name;
    backgroundColor;
    backgroundAlpha;
    _canvasContainer;
    _parameters;
    _computedParameters = { height: 0, left: 0, top: 0, width: 0, topReversed: 0 };
    _updateCameraSize;
    constructor(parameters) {
        this.camera = parameters.camera;
        this.composer = parameters.composer;
        this.scene = parameters.scene;
        this.scene.add(this.camera);
        this.visible = parameters.visible ?? true;
        this._parameters = parameters.viewportParameters;
        this.name = parameters.name ?? "";
        this._updateCameraSize = parameters.updateCameraSize || ((width, height) => CameraUtils.updateCameraSize(width, height, this.camera));
        this.backgroundColor = (typeof parameters.backgroundColor == "number" ? new Color(parameters.backgroundColor) : parameters.backgroundColor) ?? new Color(0x000000);
        this.backgroundAlpha = parameters.backgroundAlpha ?? 1;
    }
    set canvas(value) {
        this._canvasContainer = value.parentElement;
        this.parameters = this._parameters;
    }
    get computedParameters() {
        return this._computedParameters;
    }
    set parameters(parameters) {
        this._parameters = parameters;
        this.updateParameters();
    }
    get parameters() {
        return this._parameters;
    }
    onWindowResize() {
        this.updateParameters();
        this.composer?.setSize(this._canvasContainer.clientWidth, this._canvasContainer.clientHeight);
    }
    updateParameters() {
        this.computeViewportParameters();
        this._updateCameraSize(this._computedParameters.width, this._computedParameters.height);
    }
    computeViewportParameters() {
        let width = this._canvasContainer.clientWidth;
        let height = this._canvasContainer.clientHeight;
        this._computedParameters.height = Math.floor(height * this._parameters.height);
        this._computedParameters.left = Math.floor(width * this._parameters.left);
        this._computedParameters.top = Math.floor(height * this._parameters.top);
        this._computedParameters.width = Math.floor(width * this._parameters.width);
        this._computedParameters.topReversed = Math.floor(height - height * (this._parameters.height + this._parameters.top));
    }
}
export class MultiViewsRenderer extends EventDispatcher {
    renderer;
    viewsports = [];
    _cursorOnViewport = false;
    _canvasContainer;
    _renderMultiViews = false;
    _activeViewport;
    _backgroundColor;
    _backgroundAlpha;
    _renderingScene;
    _renderingCamera;
    _currentComposer;
    constructor(renderer, backgroundColor = 0x000000, backgroundAlpha = 1) {
        super();
        this.renderer = renderer;
        this._canvasContainer = this.renderer.domElement.parentElement;
        this._backgroundAlpha = backgroundAlpha;
        this.backgroundColor = typeof backgroundColor == "number" ? new Color(backgroundColor) : backgroundColor;
    }
    set cursorOnViewport(value) {
        if (this._cursorOnViewport !== value) {
            this._cursorOnViewport = value;
            this.dispatchEvent({
                type: "cursorOnCanvas",
                data: { value }
            });
        }
    }
    get renderingScene() {
        return this._renderingScene;
    }
    get activeScene() {
        return this._activeViewport ? this._activeViewport.scene : this._renderingScene;
    }
    get activeCamera() {
        return this._activeViewport ? this._activeViewport.camera : this._renderingCamera;
    }
    get isMultiViewsActive() {
        return this._renderMultiViews;
    }
    set isMultiViewsActive(value) {
        if (value != this._renderMultiViews) {
            this._renderMultiViews = value;
            this.renderer.setScissorTest(value);
            if (!value) {
                this.renderer.setSize(this._canvasContainer.clientWidth, this._canvasContainer.clientHeight); //necessary even if parameters are equals after disable multiviews
                this.renderer.setClearColor(this._backgroundColor, this._backgroundAlpha);
                this.activeViewport = undefined;
                this._renderingScene.needsRender = true;
            }
            else {
                this.activeViewport = this.viewsports[0];
                for (const viewport of this.viewsports) {
                    viewport.scene.needsRender = true;
                }
            }
            this.dispatchEvent({ type: "multiViewsSwitched", data: { value: value } });
        }
    }
    set activeViewport(viewport) {
        if (this._activeViewport != viewport) {
            const oldViewport = this._activeViewport;
            this._activeViewport = viewport;
            this.dispatchEvent({
                type: "viewportSwitched",
                data: {
                    oldViewport: oldViewport,
                    newViewport: viewport
                }
            });
        }
    }
    get activeViewport() {
        return this._activeViewport;
    }
    set backgroundColor(color) {
        this._backgroundColor = color;
        this.renderer.setClearColor(this._backgroundColor, this._backgroundAlpha);
    }
    get backgroundColor() {
        return this._backgroundColor;
    }
    set backgroundAlpha(value) {
        this._backgroundAlpha = value;
        this.renderer.setClearColor(this._backgroundColor, this._backgroundAlpha);
    }
    get backgroundAlpha() {
        return this._backgroundAlpha;
    }
    /**
     * Set the scene that will be rendered if the property 'renderMultiViews' is false.
     * It can be changed only if 'renderMultiViews' is false.
     */
    setRenderingScene(scene, camera, composer) {
        if (!this._renderMultiViews && this._renderingScene != scene) {
            const oldScene = this._renderingScene;
            this._renderingScene = scene;
            this._renderingCamera = camera;
            this._currentComposer = composer;
            this.dispatchEvent({
                type: "sceneSwitched",
                data: {
                    oldScene: oldScene,
                    newScene: scene
                }
            });
            scene.needsRender = true;
        }
    }
    onWindowResize() {
        for (const viewport of this.viewsports) {
            viewport.onWindowResize();
        }
    }
    updateSelectedView(mouse) {
        this.activeViewport = this.getViewport(mouse);
    }
    getViewport(mouse) {
        for (const view of this.viewsports) { //todo this could be optimized a bit
            if (view.visible &&
                view.computedParameters.left - 1 <= mouse.x &&
                view.computedParameters.left + view.computedParameters.width + 1 >= mouse.x &&
                view.computedParameters.topReversed - 1 <= mouse.y &&
                view.computedParameters.topReversed + view.computedParameters.height + 1 >= mouse.y) {
                return view;
            }
        }
        return undefined;
    }
    render() {
        if (this._renderMultiViews) {
            let rendered = false;
            for (const viewport of this.viewsports) {
                rendered = this.renderElementMultiView(viewport) || rendered;
            }
            return rendered;
        }
        else if (this._renderingScene.needsRender) {
            if (this._currentComposer) {
                this._currentComposer.render();
            }
            else {
                this.renderer.render(this._renderingScene, this._renderingCamera);
            }
            return true;
        }
        return false;
    }
    renderElementMultiView(element) {
        if (element.visible && element.scene.needsRender) {
            let parameters = element.computedParameters;
            this.renderer.setViewport(parameters.left, parameters.top, parameters.width, parameters.height);
            this.renderer.setScissor(parameters.left, parameters.top, parameters.width, parameters.height);
            this.renderer.setClearColor(element.backgroundColor ?? this._backgroundColor, element.backgroundAlpha ?? this._backgroundAlpha);
            if (element.composer) {
                element.composer.render();
            }
            else {
                this.renderer.render(element.scene, element.camera);
            }
            return true;
        }
        return false;
    }
    add(...mvElements) {
        for (let element of mvElements) {
            element.canvas = this.renderer.domElement;
            this.viewsports.push(element);
        }
    }
    remove(mvElement) {
        for (let i = 0; i < this.viewsports.length; i++) {
            if (mvElement == this.viewsports[i]) {
                this.viewsports.splice(i, 1);
                return;
            }
        }
    }
    getRaycasterCoords(mouse, target = { x: 0, y: 0 }) {
        if (this._renderMultiViews) {
            this.getRaycasterCoordsMultiviews(mouse, this._activeViewport, target);
            if (Math.max(Math.abs(target.x), Math.abs(target.y)) > 1) {
                this.cursorOnViewport = false;
                target.x = Math.max(-1, Math.min(1, target.x));
                target.y = Math.max(-1, Math.min(1, target.y));
            }
            else {
                this.cursorOnViewport = true;
            }
        }
        else {
            target.x = (mouse.x / this._canvasContainer.clientWidth) * 2 - 1;
            target.y = -(mouse.y / this._canvasContainer.clientHeight) * 2 + 1;
        }
        return target;
    }
    getRaycasterCoordsMultiviews(mouse, mvElement, target) {
        target.x = ((mouse.x - mvElement.computedParameters.left) / mvElement.computedParameters.width) * 2 - 1;
        target.y = -((mouse.y - mvElement.computedParameters.topReversed) / mvElement.computedParameters.height) * 2 + 1;
        return target;
    }
}
//# sourceMappingURL=MultiViewRenderer.js.map